export interface IPromptVariable {
  label: string;
  value: string;
  description: string;
  example: string;
  example2?: string;
}

export interface IVariableGroup {
  group: string;
  variables: IPromptVariable[];
}

export const PROMPT_VARIABLE_GROUPS: IVariableGroup[] = [
  {
    group: "Freelancer",
    variables: [
      {
        label: "Freelancer Name",
        value: "[Freelancer Name]",
        description: "Your full name from Upwork profile",
        example: "Roman B.",
      },
      {
        label: "Hourly Rate",
        value: "[Hourly Rate]",
        description: "Your hourly rate from the job page",
        example: "$45/hr",
      },
      {
        label: "Skills",
        value: "[Skills]",
        description: "Your top skills from Upwork profile",
        example: "React, TypeScript, Node.js",
      },
      {
        label: "Freelancer Country",
        value: "[Freelancer Country]",
        description: "Your country from Upwork profile",
        example: "Ukraine",
      },
      {
        label: "Freelancer City",
        value: "[Freelancer City]",
        description: "Your city from Upwork profile",
        example: "Kyiv",
      },
      {
        label: "Freelancer Timezone",
        value: "[Freelancer Timezone]",
        description: "Your timezone from Upwork profile",
        example: "UTC+02:00 Kyiv",
      },
    ],
  },
  {
    group: "Client",
    variables: [
      {
        label: "Client Name",
        value: "[Client Name]",
        description: "Client's name from the job posting, if available",
        example: "John",
      },
      {
        label: "Client Country",
        value: "[Client Country]",
        description: "Client's country from the job posting",
        example: "United States",
      },
      {
        label: "Client City",
        value: "[Client City]",
        description: "Client's city from the job posting",
        example: "San Francisco",
      },
      {
        label: "Client Greeting",
        value: "[Client Greeting]",
        description: "Greeting adapted to the client's country and name",
        example: "Hi John, greetings from Kyiv!",
      },
      {
        label: "Client Pain Point",
        value: "[Client Pain Point]",
        description: "The main problem the client needs solved",
        example: "slow page load times affecting user retention",
      },
    ],
  },
  {
    group: "Proposal",
    variables: [
      {
        label: "Opening Question",
        value: "[OpeningQuestion]",
        description: "Smart question about the job that shows you actually read the posting",
        example: "Are you rebuilding from scratch or migrating the existing codebase?",
        example2: "Is this a one-time redesign or do you need ongoing design support?",
      },
      {
        label: "Problem Mirror",
        value: "[ProblemMirror]",
        description: "Restates the client's problem in your own words to show you get it",
        example: "Your dashboard takes 8+ seconds to load and users are dropping off.",
        example2: "Your Shopify store conversions dropped after the last theme update.",
      },
      {
        label: "Proof Point",
        value: "[ProofPoint]",
        description: "A specific result from your past work that matches this job",
        example: "Cut load time from 8s to 400ms on a React dashboard with 50K rows.",
        example2: "Redesigned checkout flow for an e-commerce client -conversions went up 34%.",
      },
      {
        label: "Micro First Step",
        value: "[MicroFirstStep]",
        description: "One concrete thing you'd do on day one to show immediate value",
        example: "I'd profile your queries and send you a bottleneck report within hours.",
        example2: "I'll audit your current landing page and share 3 quick wins by tomorrow.",
      },
      {
        label: "Availability Match",
        value: "[AvailabilityMatch]",
        description: "Your availability matched to what the client needs. Skipped if not relevant",
        example: "Available to start tomorrow, 30+ hrs/week.",
        example2: "I can dedicate 20 hrs/week starting Monday.",
      },
      {
        label: "Budget Rate Fit",
        value: "[BudgetRateFit]",
        description: "How your rate compares to the client's budget and market average",
        example: "My $45/hr fits your budget -similar projects averaged $50/hr.",
        example2: "At $60/hr this falls within your $3K-5K range for the scope described.",
      },
      {
        label: "Budget Ack",
        value: "[BudgetAck]",
        description: "Confirms the budget works for the scope. Skipped if no budget posted",
        example: "Your $2K budget works -a similar project took me about 35 hours.",
        example2: "The $500 fixed price is fair for this scope, I can deliver within it.",
      },
      {
        label: "Requirement Ack",
        value: "[RequirementAck]",
        description: "Confirms specific requirements the client marked as important",
        example: "Noted on the NDA -happy to sign before we start.",
        example2: "Yes, I have experience with HIPAA-compliant systems as you require.",
      },
      {
        label: "Timezone Overlap",
        value: "[TimezoneOverlap]",
        description: "Shared working hours with the client. Skipped if not mentioned",
        example: "We have 5 hours of overlap (10am-3pm your time).",
        example2: "I'm in UTC+2, so we overlap 9am-1pm EST for daily syncs.",
      },
      {
        label: "CTA",
        value: "[CTA]",
        description: "Closing line -suggests a next step or references your opening question",
        example: "Happy to jump on a quick call to discuss the migration approach.",
        example2: "Want me to send a quick Loom walkthrough of how I'd tackle this?",
      },
    ],
  },
];

export const PROMPT_VARIABLES: IPromptVariable[] =
  PROMPT_VARIABLE_GROUPS.flatMap((g) => g.variables);
